import { BALES_UNIT, NUMERIC_COMPARISON_EPSILON, parseNumeric } from './numberFormatUtils';

/**
 * Recipe normalization layer.
 *
 * This module is the single place that knows how the two production types differ.
 * It turns raw Firestore shapes (run-level production properties, correction
 * materials, global ingredient metadata) into a uniform, display-ready item shape
 * so view components never branch on production type or compute correction deltas.
 *
 * Production types:
 * - batching   -> per-ingredient stat is a weight read from the `batch_size` field family (unit `t`)
 * - continuous -> per-ingredient stat is a ratio read from the `ratio` field family (unit `%`)
 *
 * A continuous run flagged `recipe_in_bales` shows the whole `bales` saved next to each ratio instead
 * (unit `bales`), in the live panel and the corrections modal alike. Batching runs keep tonnes.
 *
 * Conveyor scoping: each ingredient carries a `device_id` naming the conveyor it is fed on (empty =
 * any conveyor). Run properties and correction materials are filtered to the active conveyor, and
 * continuous ratios rescaled to 100 % over that subset.
 *
 * Conveyor reassignment: a correction material's own `device_id` overrides the run's assignment, so
 * a correction can move an ingredient between conveyors. The conveyor it moves to shows it as corrected;
 * the conveyor it leaves keeps the row at zero. An empty `device_id` keeps the run's assignment.
 *
 * Functions here are intentionally pure and side-effect free.
 */

export const PRODUCTION_TYPE = Object.freeze({
  BATCHING: 'batching',
  CONTINUOUS: 'continuous',
});

/**
 * Resolve the production type from a production run.
 *
 * @param {Object|null} production - Production run (carries the `batching` boolean flag).
 * @returns {'batching'|'continuous'} Production type.
 */
export function getProductionType(production) {
  return production?.batching ? PRODUCTION_TYPE.BATCHING : PRODUCTION_TYPE.CONTINUOUS;
}

/**
 * Resolve which field family + unit to read for a production type.
 *
 * The same spec drives both run properties and correction materials:
 * - `valueKey`    -> the displayed/new value (`batch_size` or `ratio`)
 * - `previousKey` -> the pre-correction value (`previous_batch_size` or `previous_ratio`)
 * - `unit`        -> display unit (`t` or `%`)
 *
 * @param {Object|null} production - Production run.
 * @returns {{valueKey: string, previousKey: string, unit: string}} Measure spec.
 */
export function getMeasureSpec(production) {
  if (production?.batching) {
    return { valueKey: 'batch_size', previousKey: 'previous_batch_size', unit: 't' };
  }
  return { valueKey: 'ratio', previousKey: 'previous_ratio', unit: '%' };
}

/**
 * Filter conveyor-tagged entries down to one conveyor: those assigned to `deviceId` plus unassigned
 * ones. A falsy `deviceId` (all-devices mode) keeps everything.
 *
 * @param {Array<Object>} properties - Entries carrying an optional `device_id`.
 * @param {string} deviceId - Active conveyor/device id.
 * @returns {Array<Object>} Entries relevant to the conveyor.
 */
export function filterPropertiesForDevice(properties, deviceId) {
  if (!Array.isArray(properties)) return [];
  if (!deviceId) return properties;
  return properties.filter((property) => !property?.device_id || property.device_id === deviceId);
}

/**
 * Index the run's conveyor assignment by ingredient id.
 *
 * An ingredient present in the run but assigned to no conveyor maps to `''` (fed on any conveyor);
 * an ingredient absent from the run has no entry at all. The two cases differ — see
 * `getMaterialsMovedOffDevice`.
 *
 * @param {Array<Object>} productionProperties - Run-level ingredient entries.
 * @returns {Map<string, string>} Conveyor id (possibly `''`) by ingredient id.
 */
export function buildRecipeDeviceLookup(productionProperties) {
  return new Map(
    (Array.isArray(productionProperties) ? productionProperties : [])
      .filter((prop) => prop?.ingredient_id)
      .map((prop) => [prop.ingredient_id, prop.device_id || '']),
  );
}

/**
 * Resolve the conveyor a correction material applies to.
 *
 * The material's own `device_id` wins: a correction may reassign an ingredient to another conveyor,
 * and that reassignment is itself part of the correction. An empty or absent `device_id` means
 * the manager left the assignment alone, so the run's conveyor for that ingredient applies — which
 * is also what corrections written before materials carried a `device_id` need.
 *
 * @param {Object} material - Correction material entry.
 * @param {Map<string, string>} recipeDeviceByIngredientId - Run conveyor assignment by ingredient id.
 * @returns {string} Conveyor id, or `''` when the ingredient belongs to no particular conveyor.
 */
export function resolveMaterialDeviceId(material, recipeDeviceByIngredientId) {
  return material?.device_id || recipeDeviceByIngredientId?.get(material?.ingredient_id) || '';
}

/**
 * Whether a correction reassigns an ingredient to a different conveyor than the run gave it.
 *
 * A conveyor move is a correction in its own right, so it sets the `corrected` flag even when the
 * ingredient's value is untouched.
 *
 * @param {Object} material - Correction material entry.
 * @param {Map<string, string>} recipeDeviceByIngredientId - Run conveyor assignment by ingredient id.
 * @returns {boolean} True when the material names a conveyor other than the run's.
 */
export function hasMovedConveyors(material, recipeDeviceByIngredientId) {
  const declaredDeviceId = material?.device_id || '';
  if (!declaredDeviceId) return false;
  if (!recipeDeviceByIngredientId?.has(material?.ingredient_id)) return false;
  return recipeDeviceByIngredientId.get(material.ingredient_id) !== declaredDeviceId;
}

/**
 * Filter correction materials down to one conveyor, honouring reassignments.
 *
 * @param {Array<Object>} materials - Correction material entries.
 * @param {Array<Object>} productionProperties - Run-level ingredient entries (conveyor assignment).
 * @param {string} deviceId - Active conveyor/device id.
 * @returns {Array<Object>} Materials relevant to the conveyor.
 */
export function filterMaterialsForDevice(materials, productionProperties, deviceId) {
  if (!Array.isArray(materials)) return [];
  if (!deviceId) return materials;

  const recipeDeviceByIngredientId = buildRecipeDeviceLookup(productionProperties);

  return materials.filter((material) => {
    const assignedDeviceId = resolveMaterialDeviceId(material, recipeDeviceByIngredientId);
    return !assignedDeviceId || assignedDeviceId === deviceId;
  });
}

/**
 * Find the materials a correction moves *off* the active conveyor.
 *
 * These ingredients were fed on this conveyor by the recipe but the correction sends them elsewhere,
 * so the conveyor keeps a zeroed row for them (see `buildDisplayItems`) rather than silently dropping
 * one. Ingredients the run never listed are skipped — there is no row of theirs to strike out.
 *
 * @param {Array<Object>} materials - Correction material entries.
 * @param {Array<Object>} productionProperties - Run-level ingredient entries (conveyor assignment).
 * @param {string} deviceId - Active conveyor/device id.
 * @returns {Array<Object>} Materials leaving the conveyor.
 */
export function getMaterialsMovedOffDevice(materials, productionProperties, deviceId) {
  if (!Array.isArray(materials) || !deviceId) return [];

  const recipeDeviceByIngredientId = buildRecipeDeviceLookup(productionProperties);

  return materials.filter((material) => {
    if (!recipeDeviceByIngredientId.has(material?.ingredient_id)) return false;

    const recipeDeviceId = recipeDeviceByIngredientId.get(material.ingredient_id);
    const wasOnThisConveyor = !recipeDeviceId || recipeDeviceId === deviceId;
    if (!wasOnThisConveyor) return false;

    const assignedDeviceId = resolveMaterialDeviceId(material, recipeDeviceByIngredientId);
    return Boolean(assignedDeviceId) && assignedDeviceId !== deviceId;
  });
}

/**
 * Rescale display item values to whole percentages summing to exactly 100 (continuous only).
 *
 * The spare percent is distributed by largest remainder so the panel never shows 99 % or 101 %;
 * ties go to the earlier entry. Only `value` is rescaled — the correction fields keep their
 * absolute run-level figures. A zero or non-finite total is left untouched.
 *
 * @param {Array<Object>} items - Uniform display items.
 * @returns {Array<Object>} Items with `value` as whole percentages totalling 100.
 */
export function renormalizeRatios(items) {
  if (!Array.isArray(items) || !items.length) return [];

  const total = items.reduce((sum, item) => sum + parseNumeric(item?.value, 0), 0);
  if (!Number.isFinite(total) || Math.abs(total) < NUMERIC_COMPARISON_EPSILON) return items;

  const exact = items.map((item) => (parseNumeric(item?.value, 0) * 100) / total);
  const values = exact.map((value) => Math.floor(value));
  const spare = 100 - values.reduce((sum, value) => sum + value, 0);

  // Largest discarded fraction first; index breaks ties.
  const byRemainder = exact
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);

  byRemainder.slice(0, Math.max(0, spare)).forEach(({ index }) => (values[index] += 1));

  return items.map((item, index) => ({ ...item, value: values[index] }));
}

/**
 * Build a lookup by ingredient id combining:
 * - run-level production properties (recipe values used in the run)
 * - global ingredient metadata (names/fields/colors)
 *
 * @param {Array<Object>} productionProperties - Run-level ingredient entries.
 * @param {Object<string, Object>} ingredients - Global ingredient documents by id.
 * @returns {Object<string, Object>} Merged ingredient lookup keyed by ingredient id.
 */
export function buildIngredientLookup(productionProperties, ingredients) {
  const ingredientById = {};

  if (Array.isArray(productionProperties)) {
    productionProperties.forEach((prop) => {
      if (!prop?.ingredient_id) return;
      ingredientById[prop.ingredient_id] = prop;
    });
  }

  if (ingredients && typeof ingredients === 'object' && !Array.isArray(ingredients)) {
    Object.entries(ingredients).forEach(([ingredientId, ingredient]) => {
      if (!ingredientId || !ingredient || typeof ingredient !== 'object') return;
      ingredientById[ingredientId] = {
        ...(ingredientById[ingredientId] || {}),
        ...ingredient,
      };
    });
  }

  return ingredientById;
}

/**
 * Normalize one run-level production property into the uniform display item shape.
 *
 * @param {Object} prop - Run-level ingredient entry.
 * @param {{valueKey: string, unit: string}} spec - Measure spec for the production type.
 * @returns {Object} Uniform display item.
 */
export function normalizeProperty(prop, spec) {
  return {
    ingredientId: prop?.ingredient_id ?? null,
    name: prop?.name ?? null,
    fields: prop?.fields ?? null,
    value: parseNumeric(prop?.[spec.valueKey], 0),
    unit: spec.unit,
    corrected: false,
    previousValue: null,
    newValue: null,
    delta: null,
    bales: getSavedBales(prop),
  };
}

/**
 * Normalize one correction material into the uniform display item shape, computing
 * previous/new/delta values and the corrected flag for the active production type.
 *
 * The explicit previous value stored on the event takes priority over the current
 * recipe value, so cross-production corrections display the correct "before" value.
 *
 * @param {Object} material - Correction material entry.
 * @param {Object} [ingredient={}] - Merged ingredient metadata for this material.
 * @param {{valueKey: string, previousKey: string, unit: string}} spec - Measure spec.
 * @returns {Object} Uniform display item with correction deltas.
 */
export function normalizeCorrectionMaterial(material, ingredient = {}, spec) {
  const previousValue = parseNumeric(material?.[spec.previousKey] ?? ingredient?.[spec.valueKey], 0);
  const newValue = parseNumeric(material?.[spec.valueKey], previousValue);
  const delta = newValue - previousValue;

  return {
    ingredientId: material?.ingredient_id ?? ingredient?.ingredient_id ?? null,
    name: ingredient?.name ?? material?.name ?? null,
    fields: ingredient?.fields ?? material?.fields ?? null,
    value: newValue,
    unit: spec.unit,
    corrected: Math.abs(delta) > NUMERIC_COMPARISON_EPSILON,
    previousValue,
    newValue,
    delta,
    bales: getSavedBales(material),
    background_color: ingredient?.background_color ?? material?.background_color ?? null,
  };
}

/**
 * Collect the conveyor's display items in the run's measure family, before any rescaling. Each item
 * also carries its saved `bales` (or null), read by the bales display.
 *
 * Always produces a single, deduplicated row per ingredient:
 * - No pending correction  -> the base recipe properties.
 * - batching production    -> the corrected materials redefine the batch (replace the recipe).
 * - continuous production  -> corrected materials are merged in place over the base recipe,
 *                             keeping recipe order; corrected ingredients show corrected values,
 *                             and any corrected ingredient not in the recipe is appended.
 *
 * Reassignment adds two rows to that picture: an ingredient moved onto this conveyor is appended
 * like any corrected ingredient the recipe omits, and one moved off keeps its recipe position
 * at zero, marked corrected.
 *
 * @param {Object} params
 * @param {Object|null} params.production - Currently running production run.
 * @param {Object|null} params.pendingCorrection - Device-scoped pending material correction (or null).
 * @param {Object<string, Object>} params.ingredients - Global ingredient metadata by id.
 * @param {string} [params.deviceId] - Active conveyor/device id; omit to keep the whole run.
 * @returns {Array<Object>} Uniform display items for the conveyor.
 */
function collectDisplayItems({ production, pendingCorrection, ingredients, deviceId }) {
  const spec = getMeasureSpec(production);
  const productionProperties = production?.production_properties;
  const baseProperties = filterPropertiesForDevice(productionProperties, deviceId);
  const baseItems = baseProperties.map((prop) => normalizeProperty(prop, spec));

  const allMaterials = Array.isArray(pendingCorrection?.materials) ? pendingCorrection.materials : [];
  if (!allMaterials.length) return baseItems;

  const ingredientById = buildIngredientLookup(productionProperties, ingredients);
  const recipeDeviceByIngredientId = buildRecipeDeviceLookup(productionProperties);
  const normalize = (material) => {
    const item = normalizeCorrectionMaterial(material, ingredientById[material?.ingredient_id] || {}, spec);
    // A conveyor move is a correction even at an unchanged value.
    return hasMovedConveyors(material, recipeDeviceByIngredientId) ? { ...item, corrected: true } : item;
  };

  const correctedItems = filterMaterialsForDevice(allMaterials, productionProperties, deviceId).map(normalize);
  // Ingredients leaving this conveyor keep their row at zero so the operator sees them stop.
  const vacatedItems = getMaterialsMovedOffDevice(allMaterials, productionProperties, deviceId)
    .map(normalize)
    .map((item) => ({ ...item, value: 0, newValue: 0, delta: -item.previousValue, corrected: true }));

  if (!correctedItems.length && !vacatedItems.length) return baseItems;

  if (production?.batching) return [...correctedItems, ...vacatedItems];

  // One row per ingredient — corrected entries replace their recipe counterpart in place, and
  // corrected ingredients absent from the recipe are appended.
  const correctedByIngredientId = new Map(
    [...correctedItems, ...vacatedItems].map((item) => [item.ingredientId, item]),
  );
  const mergedItems = baseItems.map((item) => correctedByIngredientId.get(item.ingredientId) || item);

  const baseIngredientIds = new Set(baseItems.map((item) => item.ingredientId));
  const newCorrectedItems = correctedItems.filter((item) => !baseIngredientIds.has(item.ingredientId));

  return [...mergedItems, ...newCorrectedItems];
}

/**
 * Whether a run is shown in bales: flagged `recipe_in_bales` and continuous. Batching runs keep tonnes.
 *
 * @param {Object|null} production - Production run.
 * @returns {boolean} True when the run's recipe is shown in its saved bales.
 */
function isShownInBales(production) {
  return production?.recipe_in_bales === true && !production?.batching;
}

/**
 * Saved bales per ingredient over the *whole* recipe, with the correction (if any) merged in.
 *
 * The single bale source for both the live panel and the corrections modal, so the two always agree.
 * Bales come from the run's `production_properties` and the correction's materials, which always store
 * them next to `ratio` for a recipe in bales; an entry without saved bales counts as 0.
 *
 * @param {Object|null} production - Production run.
 * @param {Object|null} correction - Correction to merge over the recipe, or null for the plain recipe.
 * @param {Object<string, Object>} ingredients - Global ingredient metadata by id.
 * @returns {Map<string, number>} Bales by ingredient id.
 */
function getRecipeBalesByIngredientId(production, correction, ingredients) {
  const items = collectDisplayItems({ production, pendingCorrection: correction, ingredients, deviceId: null });
  return new Map(items.map((item) => [item.ingredientId, item.value > 0 ? (item.bales ?? 0) : 0]));
}

/**
 * The whole bales a recipe property or correction material was entered with, if saved.
 *
 * @param {Object} entry - Run production property or correction material.
 * @returns {number|null} Saved bales, or null when the entry carries none.
 */
function getSavedBales(entry) {
  const bales = Number(entry?.bales);
  return entry?.bales != null && Number.isFinite(bales) ? bales : null;
}

/**
 * Build display items for a run shown in bales (see `isShownInBales`).
 *
 * Bales are the saved whole counts over the *whole* recipe with the pending correction merged in (see
 * `getRecipeBalesByIngredientId`), so every conveyor shows the same count for an ingredient as the
 * dashboard does; the conveyor subset is then picked out without any rescaling. Only `value` and `unit`
 * change.
 *
 * @param {Object} params - Same as `buildDisplayItems`.
 * @returns {Array<Object>} Display items with `value` in whole bales and `unit` `bales`.
 */
function buildBaleDisplayItems({ production, pendingCorrection, ingredients, deviceId }) {
  const balesByIngredientId = getRecipeBalesByIngredientId(production, pendingCorrection, ingredients);

  return collectDisplayItems({ production, pendingCorrection, ingredients, deviceId }).map((item) => ({
    ...item,
    // Vacated rows stay at zero: the ingredient is no longer fed on this conveyor.
    value: item.value > 0 ? (balesByIngredientId.get(item.ingredientId) ?? 0) : 0,
    unit: BALES_UNIT,
  }));
}

/**
 * Build the ordered list of display items for the live recipe panel, scoped to one conveyor.
 *
 * - batching production   -> weights from the `batch_size` family; a correction replaces the batch.
 * - continuous production -> ratios merged with any correction, then rescaled to 100 % over the
 *                            conveyor subset (the rescale runs last, after the merge).
 * - continuous in bales  -> saved bales over the whole recipe, never rescaled (`buildBaleDisplayItems`);
 *                            a batching run flagged `recipe_in_bales` keeps tonnes.
 *
 * Batching weights are absolute tonnes and never rescaled. An empty result means the run assigns
 * nothing to this conveyor.
 *
 * @param {Object} params
 * @param {Object|null} params.production - Currently running production run.
 * @param {Object|null} params.pendingCorrection - Device-scoped pending material correction (or null).
 * @param {Object<string, Object>} params.ingredients - Global ingredient metadata by id.
 * @param {string} [params.deviceId] - Active conveyor/device id; omit to keep the whole run.
 * @returns {Array<Object>} Uniform display items for the conveyor.
 */
export function buildDisplayItems({ production, pendingCorrection, ingredients, deviceId }) {
  if (isShownInBales(production)) {
    return buildBaleDisplayItems({ production, pendingCorrection, ingredients, deviceId });
  }

  const items = collectDisplayItems({ production, pendingCorrection, ingredients, deviceId });

  return production?.batching ? items : renormalizeRatios(items);
}

/**
 * Split normalized correction materials into one section per conveyor for the corrections modal.
 *
 * An ingredient the correction reassigns appears in two sections, matching what each conveyor's
 * live panel shows: zeroed in the conveyor it leaves (`movedToDeviceId` set) and as an arrival in
 * the conveyor it joins (`movedFromDeviceId` set). The arrival's `previousValue` is dropped to 0
 * because, read from the destination conveyor, the ingredient was not being fed there before — so
 * each section's totals describe that conveyor alone.
 *
 * Sections keep the order their conveyors first appear in — for a reassignment the conveyor it leaves
 * comes before the conveyor it joins — except the unassigned one (`''`), which always comes last.
 *
 * @param {Array<Object>} materials - Normalized correction materials carrying conveyor fields.
 * @returns {Array<{deviceId: string, materials: Array<Object>}>} Sections by conveyor.
 */
export function groupCorrectionMaterialsByDevice(materials) {
  const materialsByDeviceId = new Map();
  const sectionFor = (deviceId) => {
    if (!materialsByDeviceId.has(deviceId)) materialsByDeviceId.set(deviceId, []);
    return materialsByDeviceId.get(deviceId);
  };

  (Array.isArray(materials) ? materials : []).forEach((material) => {
    // `movedFromDeviceId` is `''` for a move off the unassigned pool, so compare against null.
    const movedFromDeviceId = material?.movedFromDeviceId ?? null;
    const hasMoved = movedFromDeviceId !== null;

    // The conveyor it leaves is registered first, so a move reads origin-then-destination.
    if (hasMoved) {
      sectionFor(movedFromDeviceId).push({
        ...material,
        value: 0,
        newValue: 0,
        delta: -material.previousValue,
        corrected: true,
        movedFromDeviceId: null,
        movedToDeviceId: material?.deviceId || '',
      });
    }

    sectionFor(material?.deviceId || '').push(
      hasMoved ? { ...material, previousValue: 0, delta: material.newValue, corrected: true } : material,
    );
  });

  return [...materialsByDeviceId.entries()]
    .map(([deviceId, sectionMaterials]) => ({ deviceId, materials: sectionMaterials }))
    .sort((a, b) => Number(a.deviceId === '') - Number(b.deviceId === ''));
}

/**
 * Build the converter that restates a normalized correction material in whole bales: previous over the
 * run's recipe, new over the recipe with the correction merged in (the live panel's bale source).
 *
 * @param {Object} correction - Correction event payload.
 * @param {Object} production - Production run shown in bales.
 * @param {Object<string, Object>} ingredients - Global ingredient metadata by id.
 * @returns {function(Object): Object} Maps a normalized material to its bale figures.
 */
function buildCorrectionBaleConverter(correction, production, ingredients) {
  const previousBales = getRecipeBalesByIngredientId(production, null, ingredients);
  const newBales = getRecipeBalesByIngredientId(production, correction, ingredients);

  return (item) => {
    const previousValue = previousBales.get(item.ingredientId) ?? 0;
    const newValue = newBales.get(item.ingredientId) ?? 0;
    return { ...item, value: newValue, unit: BALES_UNIT, previousValue, newValue, delta: newValue - previousValue };
  };
}

/**
 * Normalize a correction event for the corrections modal: keeps the event shape but
 * replaces its `materials` with uniform display items carrying previous/new/delta values,
 * each tagged with the conveyor it applies to plus the conveyor it came from when reassigned.
 *
 * `deviceGroups` holds the same materials split per conveyor (see `groupCorrectionMaterialsByDevice`);
 * `materials` stays one entry per correction material, so a single-conveyor correction can render
 * flat from it.
 *
 * For a run shown in bales, previous/new/delta are whole bales: previous from the run's recipe, new
 * from the recipe with this correction merged in (the saved `bales`) — the same figures the live panel
 * shows.
 *
 * @param {Object|null} correction - Correction event payload.
 * @param {Object|null} production - Production run providing type + recipe fallbacks.
 * @param {Object<string, Object>} ingredients - Global ingredient metadata by id.
 * @returns {Object|null} Correction with normalized materials (or the original when not applicable).
 */
export function normalizeCorrection(correction, production, ingredients) {
  if (!correction) return null;
  const materials = Array.isArray(correction.materials) ? correction.materials : [];
  if (!materials.length) return correction;

  const spec = getMeasureSpec(production);
  const ingredientById = buildIngredientLookup(production?.production_properties, ingredients);
  const recipeDeviceByIngredientId = buildRecipeDeviceLookup(production?.production_properties);

  const toBales = isShownInBales(production) ? buildCorrectionBaleConverter(correction, production, ingredients) : null;

  const normalizedMaterials = materials.map((material) => {
    const normalized = normalizeCorrectionMaterial(material, ingredientById[material?.ingredient_id] || {}, spec);
    const item = toBales ? toBales(normalized) : normalized;
    const hasMoved = hasMovedConveyors(material, recipeDeviceByIngredientId);

    return {
      ...item,
      deviceId: resolveMaterialDeviceId(material, recipeDeviceByIngredientId),
      movedFromDeviceId: hasMoved ? recipeDeviceByIngredientId.get(material.ingredient_id) : null,
      movedToDeviceId: null,
      corrected: item.corrected || hasMoved,
    };
  });

  return {
    ...correction,
    materials: normalizedMaterials,
    deviceGroups: groupCorrectionMaterialsByDevice(normalizedMaterials),
  };
}
